import usePeople from '@discover-data/stores/hooks/people/usePeople';
import usePeopleOptions from '@discover-data/stores/hooks/people/usePeopleOptions';
import getPeopleOptions, {
  type DinerOptions,
  type PeopleOption,
} from '@discover-data/stores/hooks/people/options';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectProps,
  type SelectTriggerProps,
} from '@discover-ui/select';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';

export type PeopleSelectProps = Omit<
  SelectProps,
  'value' | 'onValueChange' | 'dir' | 'defaultValue'
> & {
  triggerProps?: SelectTriggerProps;
  peopleOptions?: DinerOptions;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

export default function PeopleSelect({
  children,
  triggerProps,
  onOpenChange,
  peopleOptions,
  minDiners,
  maxDiners,
  ...props
}: PeopleSelectProps) {
  const [people, setPeople] = usePeople(peopleOptions, minDiners, maxDiners);
  const dinerOptions = usePeopleOptions(peopleOptions, minDiners, maxDiners);
  const selectableDinerOptions =
    Array.isArray(peopleOptions) && peopleOptions.length > 0
      ? getPeopleOptions(peopleOptions)
      : dinerOptions;

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
  };

  const handleValueChange = (newValue: string) => {
    const peopleCount = parseInt(newValue, 10);
    setPeople(peopleCount);
  };

  return (
    <TrackProvider button_name="people picker">
      <Select
        value={`${people}`}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        {...props}>
        <SelectTrigger {...triggerProps}>
          {children}
          <SelectValue placeholder="Number of people">
            {people} {people === 1 ? 'diner' : 'diners'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {dinerOptions.map((dinerOption) => (
            <SelectItem
              key={dinerOption}
              value={`${dinerOption}`}
              disabled={!selectableDinerOptions.includes(dinerOption)}>
              {dinerOption} {dinerOption === 1 ? 'diner' : 'diners'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TrackProvider>
  );
}
